/**
 * Studio Google sign-in for the onboarding flow.
 *
 * Sign-in happens in a POPUP on the studio origin. After Google OAuth the popup
 * lands on the studio's popup-complete handoff page, which `postMessage`s the
 * NextAuth session token + user back to this opener and closes. We store the
 * token (see studioToken.ts) and send it as `Authorization: Bearer` on studio
 * calls — this is what makes sign-in work when the studio session cookie is
 * blocked as a third-party cookie (Incognito / Safari ITP / 3P-cookie sunset),
 * the exact "signed in but /new still shows signed out" bug.
 *
 * Fallbacks, in order: (1) postMessage handoff (primary, cookie-independent);
 * (2) cookie-backed session poll for the same-origin case
 * (app.dreamboothstudio.com/new); (3) full-page redirect when the popup is
 * blocked — the localStorage draft survives the round-trip.
 */

import { fetchStudioSession, studioSignInUrl, type StudioSessionUser } from './onboardingApi'
import { setStudioAuth } from './studioToken'

const POLL_INTERVAL_MS = 1500
const POLL_TIMEOUT_MS = 3 * 60 * 1000

const STUDIO_ORIGIN = (): string => {
  try {
    return new URL(process.env.VITE_API_URL || 'https://dreamboothstudio.com').origin
  } catch {
    return 'https://dreamboothstudio.com'
  }
}

interface AuthMessage {
  type: 'dreambooth-auth'
  token: string
  user: StudioSessionUser
  expiresAt?: number
}

function isAuthMessage(data: unknown): data is AuthMessage {
  if (!data || typeof data !== 'object') return false
  const d = data as Record<string, unknown>
  return (
    d.type === 'dreambooth-auth' &&
    typeof d.token === 'string' &&
    !!d.user &&
    typeof (d.user as StudioSessionUser).email === 'string'
  )
}

export async function signInWithStudio(): Promise<StudioSessionUser | null> {
  // Already signed in? (stored token, or a same-origin cookie session.)
  const existing = await fetchStudioSession()
  if (existing) return existing

  const w = 480
  const h = 640
  const left = Math.max(0, (window.screen.width - w) / 2)
  const top = Math.max(0, (window.screen.height - h) / 2)
  const popup = window.open(
    studioSignInUrl(),
    'dreambooth-signin',
    `width=${w},height=${h},left=${left},top=${top},popup=1`
  )

  if (!popup) {
    // Popup blocked → full-page redirect. The draft is already persisted.
    window.location.href = studioSignInUrl().replace(
      /callbackUrl=[^&]+/,
      `callbackUrl=${encodeURIComponent(window.location.href)}`
    )
    return null
  }

  const startedAt = Date.now()
  return new Promise((resolve) => {
    let settled = false
    const studioOrigin = STUDIO_ORIGIN()

    const finish = (user: StudioSessionUser | null): void => {
      if (settled) return
      settled = true
      clearInterval(timer)
      window.removeEventListener('message', onMessage)
      try {
        popup.close()
      } catch {
        // cross-origin close can throw in odd embeds; harmless
      }
      resolve(user)
    }

    // Primary: the popup-complete page hands us {token, user} directly, so we
    // never depend on reading a third-party cookie.
    const onMessage = (event: MessageEvent): void => {
      if (event.origin !== studioOrigin) return
      if (!isAuthMessage(event.data)) return
      const { token, user, expiresAt } = event.data
      setStudioAuth({
        token,
        user,
        expiresAt: typeof expiresAt === 'number' ? expiresAt : Date.now() + 24 * 60 * 60 * 1000
      })
      finish(user)
    }
    window.addEventListener('message', onMessage)

    // Fallbacks: cookie session poll (same-origin), popup-closed, timeout.
    const timer = setInterval(async () => {
      if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
        finish(null)
        return
      }
      const user = await fetchStudioSession()
      if (user) {
        finish(user)
        return
      }
      if (popup.closed) {
        // User closed the popup — one last probe, then give up.
        finish(await fetchStudioSession())
      }
    }, POLL_INTERVAL_MS)
  })
}
